// src/pages/OfferRedirect.js
//
// Página intermediária entre o link do grupo e a oferta final.
// Visual alinhado com a landing do Mimo da Vez: a pessoa precisa reconhecer
// a marca no meio do caminho, senão o link parece suspeito.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API_BASE = "https://us-central1-achados-67630.cloudfunctions.net/api";

// Container dedicado à análise do redirect.
// Carregado só aqui (e não no index.html) de propósito: o mesmo app serve o
// painel interno, e o GTM no index.html contaria a sua própria navegação.
const GTM_ID = "GTM-KCM7CLPZ";

// Tempo na tela antes de mandar para a loja.
// Folga suficiente para o GTM despachar o achadinho_click sem segurar a
// pessoa à toa: cada segundo parado aqui é chance de desistir antes da oferta.
const REDIRECT_DELAY_MS = 2000;

// O link manual é o plano B de quem não foi redirecionado: só faz sentido
// aparecer DEPOIS do momento em que o redirect deveria ter acontecido.
const MANUAL_LINK_DELAY_MS = REDIRECT_DELAY_MS + 2000;

// Paleta da landing (hero.css do front do Mimo).
const PINK = "#f52b72";
const INK = "#171717";

function formatBRL(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function Logo() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: 58,
          height: 58,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          borderRadius: 18,
          color: "#fff",
          fontSize: 29,
          background:
            "linear-gradient(135deg, #ff5d96 0%, #f52b72 55%, #e90b5a 100%)",
          boxShadow: "0 15px 32px rgba(245, 43, 114, 0.24)",
          transform: "rotate(-3deg)",
        }}
      >
        {/* coração, mesmo símbolo do logo da landing */}
        <svg viewBox="0 0 24 24" width="29" height="29" fill="currentColor">
          <path d="M12 21s-6.7-4.35-9.33-8.02C.24 9.66 1.6 5.5 5.3 4.6c2.03-.5 3.9.36 4.98 1.86l.72 1 .72-1c1.08-1.5 2.95-2.36 4.98-1.86 3.7.9 5.06 5.06 2.63 8.38C18.7 16.65 12 21 12 21z" />
        </svg>
      </div>

      <div style={{ textAlign: "left" }}>
        <div
          style={{
            color: PINK,
            fontFamily: '"Georgia", "Times New Roman", serif',
            fontSize: 29,
            fontWeight: 700,
            fontStyle: "italic",
            lineHeight: 1,
          }}
        >
          Mimo
        </div>

        <div
          style={{
            marginTop: 6,
            color: INK,
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "4.5px",
          }}
        >
          DA VEZ
        </div>
      </div>
    </div>
  );
}

export default function OfferRedirect() {
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | going | error
  const [errorMsg, setErrorMsg] = useState("");
  const [finalUrl, setFinalUrl] = useState(null);
  const [showManual, setShowManual] = useState(false);

  /*
   * Sobe o GTM antes da busca da oferta: ele carrega em paralelo com o fetch
   * e já está pronto quando o achadinho_click é empurrado. Como o dataLayer é
   * uma fila, um push que chegue antes do script ainda é processado.
   */
  useEffect(() => {
    if (window.__GTM_LOADED__) return;
    window.__GTM_LOADED__ = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    async function loadAndRedirect() {
      try {
        const response = await fetch(`${API_BASE}/queue/item/${id}`);
        const data = await response.json();

        if (!response.ok || !data?.item) {
          setStatus("error");
          setErrorMsg("Não encontramos essa oferta.");
          return;
        }

        const loaded = data.item;
        const url =
          loaded.productUrlFinal ||
          loaded.originalUrl ||
          loaded.productUrl;

        if (!url) {
          setItem(loaded);
          setStatus("error");
          setErrorMsg("Essa oferta está sem link de destino.");
          return;
        }

        setItem(loaded);
        setFinalUrl(url);
        setStatus("going");

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "achadinho_click",
          product_id: id,
          product_name: loaded.productName,
          price_from: loaded.priceFrom,
          price_to: loaded.priceTo,
          coupon_name: loaded.couponName,
          coupon_value: loaded.couponValue,
          day: loaded.day,
          source: "achadinhos",
        });

        fetch(`${API_BASE}/queue/click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id,
            productName: loaded.productName,
            productUrlFinal: url,
            day: loaded.day,
          }),
        }).catch(() => {});

        setTimeout(() => {
          window.location.href = url;
        }, REDIRECT_DELAY_MS);
      } catch (error) {
        console.error("Erro ao carregar oferta:", error);
        setStatus("error");
        setErrorMsg("Tivemos um problema para abrir essa oferta.");
      }
    }

    if (id) loadAndRedirect();
  }, [id]);

  // Rede ruim ou navegador que bloqueia o redirect automático deixariam
  // a pessoa presa na tela: passado o prazo do redirect, oferecemos o link.
  useEffect(() => {
    const t = setTimeout(
      () => setShowManual(true),
      MANUAL_LINK_DELAY_MS
    );
    return () => clearTimeout(t);
  }, []);

  const priceTo = formatBRL(item?.priceTo);
  const priceFrom = formatBRL(item?.priceFrom);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        color: INK,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background:
          "radial-gradient(circle at 10% 15%, rgba(245,43,114,.06), transparent 28%)," +
          "radial-gradient(circle at 90% 75%, rgba(245,43,114,.04), transparent 30%)," +
          "#ffffff",
      }}
    >
      <style>{`
        @keyframes mimo-spin { to { transform: rotate(360deg); } }
        @keyframes mimo-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes mimo-pulse {
          0%, 100% { opacity: .35; }
          50% { opacity: 1; }
        }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: 420,
          animation: "mimo-in .4s ease both",
        }}
      >
        <Logo />

        <div
          style={{
            marginTop: 30,
            padding: "30px 24px",
            borderRadius: 24,
            background: "#fff",
            border: "1px solid rgba(245,43,114,.12)",
            boxShadow: "0 20px 50px rgba(245,43,114,.10)",
          }}
        >
          {status === "error" ? (
            <>
              <div style={{ fontSize: 34, marginBottom: 10 }}>💔</div>

              <div
                style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}
              >
                {errorMsg}
              </div>

              <p
                style={{
                  fontSize: 14,
                  color: "#6b6b6b",
                  lineHeight: 1.6,
                  margin: "0 0 20px",
                }}
              >
                Ela pode ter saído do ar ou o link estar incompleto. Volta no
                grupo que tem achadinho novo toda hora.
              </p>

              <a
                href="https://mimodavez.com.br"
                style={{
                  display: "inline-block",
                  padding: "13px 26px",
                  borderRadius: 999,
                  background:
                    "linear-gradient(135deg, #ff5d96 0%, #f52b72 55%, #e90b5a 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  boxShadow: "0 12px 26px rgba(245,43,114,.28)",
                }}
              >
                Ver os achadinhos do dia
              </a>
            </>
          ) : (
            <>
              {item?.imageUrl && (
                <img
                  src={item.editedImageUrl || item.imageUrl}
                  alt={item.productName || ""}
                  style={{
                    width: 108,
                    height: 108,
                    objectFit: "cover",
                    borderRadius: 18,
                    marginBottom: 16,
                    border: "1px solid rgba(0,0,0,.06)",
                  }}
                />
              )}

              {item?.productName && (
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    marginBottom: priceTo ? 6 : 18,
                  }}
                >
                  {item.productName}
                </div>
              )}

              {priceTo && (
                <div style={{ marginBottom: 18 }}>
                  {priceFrom && (
                    <span
                      style={{
                        fontSize: 14,
                        color: "#9a9a9a",
                        textDecoration: "line-through",
                        marginRight: 8,
                      }}
                    >
                      {priceFrom}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: PINK,
                    }}
                  >
                    {priceTo}
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  marginTop: 4,
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "2px solid rgba(245,43,114,.18)",
                    borderTopColor: PINK,
                    display: "inline-block",
                    animation: "mimo-spin .7s linear infinite",
                  }}
                />

                <span
                  style={{
                    fontSize: 14.5,
                    color: "#6b6b6b",
                    animation: "mimo-pulse 1.6s ease-in-out infinite",
                  }}
                >
                  {status === "loading"
                    ? "Buscando seu achadinho…"
                    : "Levando você até a oferta…"}
                </span>
              </div>

              {showManual && finalUrl && (
                <a
                  href={finalUrl}
                  style={{
                    display: "inline-block",
                    marginTop: 20,
                    fontSize: 13.5,
                    color: PINK,
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  Não abriu? Toque aqui
                </a>
              )}
            </>
          )}
        </div>

        <div
          style={{
            marginTop: 18,
            fontSize: 12,
            color: "#9a9a9a",
            letterSpacing: ".2px",
          }}
        >
          Mimo da Vez · a gente garimpa, você economiza 💕
        </div>
      </div>
    </div>
  );
}
